import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const AdminSideBar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const sidebarRef = useRef(null);

  // Handle responsiveness and auto-close on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 1024;
      setIsMobile(mobileView);
      // Only auto-close if we're switching to mobile view
      if (mobileView && !isMobile) {
        setIsOpen(false);
      } else if (!mobileView && isMobile) {
        setIsOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const menuItems = [
    { name: "User Accounts", path: "/useraccount" },
  ];

  const navItems = [
    { name: "Dashboard", path: "/dashboards" },
    { name: "Top Banner", path: "/topbannermainpage" },
    { name: "Smart Farming Solutions", path: "/smartfarmingsolutions" },
    { name: "Experience App", path: "/experienceapp" },
    { name: "Voice of Farmers", path: "/voiceoffarmersmainpage" },
    { name: "Stay Informed", path: "/stayinformed" },
    { name: "Reports", path: "/Admin_Report" },
    { name: "Services", path: "/servicespage" },
    { name: "Resources", path: "/resourcesmainpage" },
    { name: "Communities", path: "/admincommuinitesmainpage" },
    { name: "Stories", path: "/stories" },
    { name: "Communication", path: "/Admin_Communication" },
  ];

  return (
    <>
      {/* Mobile Toggle Button - Only visible on mobile */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className={` z-50 p-2 bg-[#152C24] text-white rounded-md  transition-all ${
            isOpen ? "left-[210px] top-4" : "left-4 top-4"
          }`}
          aria-label={isOpen ? "Close sidebar" : "Open sidebar"}
        >
          {isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
      )}

      {/* Sidebar - Works for both mobile and laptop */}
      <div
        ref={sidebarRef}
        className={`top-0 left-0 h-full bg-white flex flex-col z-40  transition-all duration-300 ${
          isOpen 
            ? "w-[250px] sm:w-[280px] lg:w-[350px]" 
            : "w-0 lg:w-[80px] overflow-hidden"
        }`}
        style={{ minHeight: "100vh" }}
      >
        {/* Sidebar Content */}
        <div className="flex-1 flex flex-col py-4 overflow-y-auto">
          {/* Overview section - Only shows when expanded */}
          {isOpen && (
            <div className="px-4 sm:px-5 lg:px-6 mb-2">
              <p className="text-black font-bold text-lg sm:text-xl lg:text-2xl">
                Overview
              </p>
            </div>
          )}

          {/* Main Menu Items */}
          <ul className="px-2 sm:px-3 lg:px-4 space-y-1">
            {menuItems.map((item, index) => (
              <li key={index} className="list-none">
                <Link
                  to={item.path}
                  className={`flex items-center h-12 sm:h-[70px] px-3 sm:px-4 rounded-r-md transition-colors ${
                    location.pathname === item.path || 
                    (location.pathname !== "/dashboards" && item.path === "/useraccount")
                      ? "bg-[#EDF0EF] hover:bg-[#152C24] hover:text-white"
                      : "bg-[#EDF0EF] hover:bg-[#152C24] hover:text-white"
                  }`}
                >
                  {isOpen && (
                    <span className="text-sm sm:text-base lg:text-lg font-bold truncate">
                      {item.name}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* Navigation Items */}
          <ul className="px-2 sm:px-3 lg:px-4 mt-3 space-y-1">
            {/* Dashboard Item */}
            <li className="list-none">
              <Link
                to="/dashboards"
                className={`flex items-center h-14 sm:h-14 px-3 sm:px-4 rounded-r-md transition-colors ${
                  location.pathname === "/dashboards"
                    ? "bg-[#EDF0EF] hover:bg-[#152C24] hover:text-white"
                    : "bg-[#EDF0EF] hover:bg-[#152C24] hover:text-white"
                }`}
              >
                {isOpen && (
                  <span className="text-sm sm:text-base lg:text-lg font-bold truncate">
                    Dashboard
                  </span>
                )}
              </Link>
            </li>
            
            {/* Space after Dashboard */}
            <div className="h-4"></div>
            
            {/* Other Navigation Items */}
            {navItems.slice(1).map((item, index) => (
              <li key={index} className="list-none">
                <Link
                  to={item.path}
                  className={`flex items-center h-12 sm:h-14 px-3 sm:px-4 rounded-r-md transition-colors ${
                    location.pathname === item.path
                      ? "bg-[#152C24] text-white"
                      : "bg-[#EDF0EF] hover:bg-[#152C24] hover:text-white"
                  }`}
                >
                  {isOpen && (
                    <span className="text-sm sm:text-base lg:text-lg font-bold truncate">
                      {item.name}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Settings Button - Shows icon only when collapsed */}

      </div>

      {/* Overlay for mobile - Only shows when sidebar is open on mobile */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default AdminSideBar;